import React, { useEffect, useRef, useState } from "react";
import { getStatusColor } from "../core/btw-status-colors";
import BtwStatusPill from "./btw-status-pill";

const BtwStatusMenu = ({
  current,
  options = [],
  onSelect,
  disabled = false,
  align = "left",
}) => {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const esc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  const close = () => {
    setOpen(false);
    setPicked(null);
    setComment("");
  };

  const submit = async () => {
    if (!picked || saving) return;
    setSaving(true);
    try {
      await onSelect(picked, comment.trim());
      close();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative inline-block">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={`group inline-flex h-8 items-center gap-1.5 rounded px-1 transition hover:bg-[#091E420F] ${
          disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
        }`}
      >
        <BtwStatusPill status={current} size="lg" />
        <svg
          className="mr-1 h-3.5 w-3.5 text-ink-subtlest"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {open ? (
        <div
          className={`absolute z-30 mt-1 w-80 rounded bg-white p-1 shadow-overlay ${
            align === "right" ? "right-0" : "left-0"
          }`}
        >
          <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">
            Wijzig status
          </div>
          <div className="max-h-72 overflow-y-auto">
            {options.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-ink-subtlest">
                Geen statussen beschikbaar.
              </div>
            ) : (
              options.map((opt) => {
                const isCurrent = current?._id === opt._id;
                const isPicked = picked?._id === opt._id;
                const c = getStatusColor(opt.color);
                return (
                  <button
                    key={opt._id}
                    type="button"
                    onClick={() => setPicked(opt)}
                    className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm text-ink transition ${
                      isPicked
                        ? "bg-brand-subtle text-brand"
                        : isCurrent
                        ? "bg-sunken"
                        : "hover:bg-[#091E420F]"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-full ${c.dot}`} />
                      <span className="text-ink">{opt.name}</span>
                      {opt.isTerminal ? (
                        <span className="text-[10px] uppercase tracking-wide text-ink-subtlest">
                          eindstation
                        </span>
                      ) : null}
                    </span>
                    {isCurrent ? (
                      <span className="text-[11px] text-ink-subtlest">huidig</span>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>

          {picked && picked._id !== current?._id ? (
            <div className="mt-2 border-t border-line pt-2">
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Optionele opmerking bij deze wijziging…"
                rows={2}
                className="w-full resize-none rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink placeholder:text-ink-subtlest focus:border-brand focus:outline-none"
              />
              <div className="mt-2 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={close}
                  className="h-8 rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224]"
                >
                  Annuleren
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={submit}
                  className="h-8 rounded bg-brand px-3 text-sm font-medium text-white transition hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {saving ? "Opslaan…" : "Wijzig status"}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default BtwStatusMenu;
